import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Tags } from 'lucide-react';
import { Sidebar } from '@/components/layout/Sidebar'; // ✅ Corrigido: usar named import
import { TagManager } from '@/components/tags/TagManager';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { OnboardingModal } from '@/components/onboarding/OnboardingModal';
import { useOnboarding } from '@/hooks/useOnboarding';
import { SubscriptionStatusBanner } from '@/components/subscription/SubscriptionStatusBanner';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { cn } from '@/lib/utils';

/**
 * Layout Principal da Aplicação
 * 
 * Fornece a estrutura básica com sidebar e área de conteúdo
 * para todas as páginas internas do sistema. Mantém a funcionalidade
 * do botão de categorias e controla quando deve ser exibido.
 * 
 * Componentes:
 * - Sidebar fixa com navegação
 * - Área de conteúdo principal (renderizada via Outlet)
 * - Painel deslizante de categorias (TagManager)
 */
const MainLayout = () => {
  const location = useLocation();
  const [isTagManagerOpen, setIsTagManagerOpen] = useState(false);

  // Hook para gerenciar onboarding
  const {
    needsOnboarding,
    isLoading: onboardingLoading,
    completeOnboarding
  } = useOnboarding();

  // Hook para verificar status da assinatura
  // Usado para mostrar banner de aviso e overlay de bloqueio
  const { status: subscriptionStatus } = useSubscriptionStatus();

  // Determina se o botão de categorias deve ser mostrado
  // Esconde em páginas que não fazem sentido ter categorias
  const shouldShowCategoriesButton = ![
    '/chat',
    '/configuracoes',
    '/dashboard',
    '/pipeline',
    '/contatos',
    '/agenda',
    '/mensagens-agendadas',
    '/ia',
    '/seo-perfil',
    '/analises/conversas',
    // Vendas não usa categorias: o botão flutuante cobria as ações dos registros.
    '/vendas',
  ].includes(location.pathname);

  // Determina se a página atual é o chat (para layout especial)
  const isChatPage = location.pathname === '/chat';
  // Pipeline e contatos rolam por dentro das listas, não a página inteira.
  const isPainelComRolagemInterna = location.pathname === '/pipeline' || location.pathname === '/contatos';

  // A cobrança vem antes do onboarding para não esconder os planos atrás do modal inicial.
  const isBillingPage = location.pathname === '/billing';

  // Loading de entrada no layout (onboarding/sessão da clínica)
  if (onboardingLoading) {
    return (
      <div className="h-screen bg-surface flex items-center justify-center">
        <ThinkingOrbLoader state="solving" size={64} label="Carregando..." />
      </div>
    );
  }

  return (
    <>
      {/* Modal de Onboarding */}
      <OnboardingModal
        open={needsOnboarding && !isBillingPage}
        onComplete={completeOnboarding}
      />

      <div className="h-screen bg-surface flex flex-col overflow-hidden">
        {/* 
         * Banner de Status da Assinatura  
         * Mostra avisos sobre vencimento e problemas de pagamento.
         * Aparece no topo da aplicação
         */}
        {subscriptionStatus?.showWarning && (
          <SubscriptionStatusBanner className="flex-shrink-0" />
        )}

        <div className="flex-1 flex overflow-hidden pt-14 md:pt-0">
          {/* Barra lateral fixa com navegação */}
          <Sidebar />

          {/* Conteúdo principal da aplicação */}
          <div className="flex-1 flex min-w-0">
            {isChatPage ? (/* Chat usa o mesmo respiro da sidebar (12 px, p-3) para os cartões alinharem no topo e na base; no mobile ocupa a tela toda */
              <div className="flex-1 min-w-0 md:p-3">
                <Outlet />
              </div>) : (/* Para outras páginas, mantemos o layout com padding */
              <main className={cn(
                'flex-1 min-w-0 relative p-4 md:p-6',
                isPainelComRolagemInterna ? 'flex flex-col overflow-hidden' : 'overflow-auto',
              )}>
                <Outlet />

                {/* Botão flutuante para abrir categorias - apenas em páginas que fazem sentido */}
                {shouldShowCategoriesButton && <Sheet open={isTagManagerOpen} onOpenChange={setIsTagManagerOpen}>
                  <SheetTrigger asChild>
                    <Button size="sm" variant="outline" className="fixed bottom-6 right-6 z-40 shadow-soft border-border bg-card hover:bg-muted rounded-lg">
                      <Tags size={16} className="mr-2" />
                      Categorias
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-80 p-0">
                    <SheetHeader className="p-4 border-b">
                      <SheetTitle>Gerenciar Categorias</SheetTitle>
                    </SheetHeader>
                    <div className="p-4 overflow-auto">
                      <TagManager />
                    </div>
                  </SheetContent>
                </Sheet>}
              </main>)}
          </div>
        </div>
      </div>
    </>
  );
};
export default MainLayout;
